import { PRESET_TOGGLE_ICONS, type WidgetBranding } from "@ai-guidance/shared";
import { darkenHex, hexToRgb, inkOnPrimary, inkOnSurface } from "@ai-guidance/shared";
import { loadWidgetState, saveWidgetState } from "./widgetState";
import { COMPOSER_CSS, Composer, type MicErrorKind } from "./composer";
import { Presence } from "./presence";
import { chromeCopy, type ChromeCopy } from "./uiText";
import { stripExpressiveTag } from "./voice/tags";

/**
 * Widget minimal de chat (buton flotant + panou). Trăiește într-un **shadow root**
 * (`mode: "open"`) atașat pe `.aig-root`: CSS-ul sitului gazdă nu ajunge la el, nici
 * măcar cu `!important`, iar `querySelectorAll` din light DOM nu-i vede conținutul -
 * de-aia mesajele noastre nu pot ajunge în snapshotul trimis modelului (vezi
 * `snapshot.ts`). Tot textul intră prin `textContent` - niciun HTML din config sau
 * de la agent nu e randat brut.
 * Culorile folosesc `--aig-primary`/`--aig-primary-hover` plus perechea derivată
 * pentru contrast, `--aig-on-primary` (cerneala peste culoare) și
 * `--aig-primary-ink` (aceeași culoare, lizibilă ca text pe alb) - toate setate
 * inline pe `.aig-root` când contul are custom branding, vezi `mount()`. Fallback
 * pe indigo-ul implicit pentru toate site-urile fără personalizare.
 */
const WIDGET_CSS = `
/* Everything below lives inside a shadow root, so the host page's selectors cannot
   reach it: their input {} and button {} rules stop at the boundary, and no amount of
   !important crosses it. What the boundary does NOT stop is inheritance - font, colour,
   letter-spacing and friends still flow in from whatever the site set on <body>. So the
   only reset still needed is for inherited properties, on :host.
   (No backticks in this comment: it lives inside a template literal, so one would end
   the string. Same hazard as the one recorded in docs/legal-register.md.) */
/* !important here is not a habit, it is the one place the cascade requires it. Inherited
   properties still cross into the shadow tree, so a site with a global rule marked
   !important would restyle our text through the host element. For important declarations
   the CSS scoping rules reverse the usual order and the inner tree wins - so this is the
   only way to hold the boundary. Layout properties are included for the same reason. */
:host {
  position: fixed !important; right: 20px !important; bottom: 20px !important;
  z-index: 2147483000 !important; display: block !important;
  font-family: system-ui, -apple-system, sans-serif !important;
  font-size: 13px !important; font-weight: 400 !important; font-style: normal !important;
  line-height: 1.5 !important; letter-spacing: normal !important; word-spacing: normal !important;
  text-transform: none !important; text-align: left !important; text-indent: 0 !important;
  text-shadow: none !important; white-space: normal !important; direction: ltr !important;
  color: #111 !important; visibility: visible !important;
}
/* Hiding must live HERE, not as an inline style on the host: for important declarations
   the shadow tree beats the host element's inline style (the same reversal the block
   above relies on), so an inline display:none!important would LOSE to the :host rule
   and the widget would stay visible on excluded pages. A class toggled by setHidden
   keeps both declarations in the same winning context, where the higher specificity
   of :host(.aig-hidden) decides. */
:host(.aig-hidden) { display: none !important; }
:host, :host *, :host *::before, :host *::after { box-sizing: border-box; }
/* The browser's own stylesheet still applies inside a shadow root, and it gives buttons
   and inputs their own font. Nothing to do with the host page - this is the UA default. */
.aig-close, .aig-toggle {
  font-family: inherit; appearance: none; -webkit-appearance: none; margin: 0;
}
.aig-toggle { width: 46px; height: 46px; border-radius: 50%; border: none; background: var(--aig-primary, #8b5cf6); color: var(--aig-on-primary, #fff); font-size: 22px; cursor: pointer; box-shadow: 0 4px 14px rgba(0,0,0,.25); display: flex; align-items: center; justify-content: center; overflow: hidden; }
.aig-toggle:hover { background: var(--aig-primary-hover, #7a51d8); }
.aig-toggle-logo { width: 100%; height: 100%; object-fit: cover; }
/* "Just logo": the launcher drops its circle, colour and shadow and shows only the
   owner's image. The image is letterboxed (contain, auto size, capped at the box) so a
   non-square logo keeps its aspect ratio - never stretched, never cropped. Applied only
   when a logo actually loaded (see mount); the fallback mark still needs its bubble. */
.aig-toggle--logo-only { border-radius: 0; background: transparent; box-shadow: none; }
.aig-toggle--logo-only:hover { background: transparent; }
.aig-toggle--logo-only .aig-toggle-logo { width: auto; height: auto; max-width: 100%; max-height: 100%; object-fit: contain; }
/* The launcher sits on someone else's page, so it stays still at rest - a bubble that
   animates forever in the corner is a distraction and a battery cost. The motion is
   spent on hover instead: the comet surges along its own axis. */
.aig-toggle svg { transition: transform .35s cubic-bezier(.34,1.56,.64,1); }
.aig-toggle:hover svg { transform: translate(1px, -1px) scale(1.1); }
@media (prefers-reduced-motion: reduce) {
  .aig-toggle svg { transition: none; }
  .aig-toggle:hover svg { transform: none; }
}
.aig-toggle[hidden] { display: none; }
/* The transform is how the panel steps aside when the thing being pointed at turns out to
   be behind it (guide.ts decides the direction; shiftPanel applies it). It has to be a
   transform rather than left/top: those two are the visitor's own, set by dragging and
   remembered in storage, and borrowing them for a temporary move would either fight the
   drag or persist a position nobody chose.
   bottom: 20px - same as the launcher's :host offset, not a reservation above it: the
   launcher is hidden the moment the panel opens (setOpen), so nothing sits below the panel
   to leave room for. A stale 84px offset once left an empty gap where the icon used to be. */
.aig-panel { position: fixed; right: 20px; bottom: 20px; width: 320px; height: 440px; min-width: 280px; min-height: 320px; max-width: 90vw; max-height: 90vh; background: #fff; color: #111; border-radius: 12px; box-shadow: 0 8px 30px rgba(0,0,0,.25); display: flex; flex-direction: column; overflow: hidden; resize: both; transition: transform .42s cubic-bezier(.22,.61,.36,1); }
/* Reduced motion keeps the move - it is what makes the highlight visible - and drops only
   the glide: the panel is simply already elsewhere. */
@media (prefers-reduced-motion: reduce) { .aig-panel { transition: none; } }
.aig-panel[hidden] { display: none; }
/* Docked: on a phone the open panel covers ~60% of the screen. When the element being
   pointed at would sit under it and no sideways move frees it, guide.ts slides the whole
   panel down (the same transform as shiftPanel) until only the header is left on screen.
   The header then reads as a handle: a chevron says "tap to open", and tapping it brings
   the panel back (releaseDock). */
.aig-panel--docked .aig-header { cursor: pointer; }
.aig-panel--docked .aig-header::after { content: "\\25B2"; font-size: 10px; opacity: .85; margin-left: auto; padding-left: 8px; }
/* --aig-on-primary is the ink that sits ON the customer's colour, picked from that
   colour's luminance in mount() - white text on a pale brand yellow scored 1.53:1,
   well under the 4.5:1 AA threshold, so it is chosen rather than assumed. */
.aig-header { padding: 10px 8px 10px 14px; background: var(--aig-primary, #8b5cf6); color: var(--aig-on-primary, #fff); font-weight: 600; font-size: 13px; display: flex; align-items: center; justify-content: space-between; gap: 8px; cursor: move; user-select: none; touch-action: none; flex-shrink: 0; }
.aig-header-left { display: flex; align-items: center; gap: 8px; min-width: 0; }
.aig-header-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* The button's own tint follows the ink too: a white wash reads well on a deep header
   and turns into a pale smudge on a light one, so it flips with --aig-on-primary. */
.aig-close { flex-shrink: 0; width: 24px; height: 24px; border: none; background: var(--aig-on-primary-soft, rgba(255,255,255,.16)); color: var(--aig-on-primary, #fff); border-radius: 6px; font-size: 14px; line-height: 1; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0; }
.aig-close:hover { background: var(--aig-on-primary-soft-hover, rgba(255,255,255,.28)); }
/* The voice toggle shares the close button's shape on purpose: same row, same
   weight, no competition. Only its state differs - while a conversation is open
   it stays lit, because a microphone the visitor forgot was on is a real
   problem, not a styling detail. */
.aig-voice { margin-right: 6px; }
.aig-voice--on { background: var(--aig-on-primary, #fff); color: var(--aig-primary, #8b5cf6); }
.aig-voice--on:hover { background: var(--aig-on-primary, #fff); }
.aig-messages { flex: 1; min-height: 0; overflow-y: auto; padding: 10px; display: flex; flex-direction: column; gap: 8px; }
.aig-msg { padding: 8px 10px; border-radius: 10px; font-size: 13px; line-height: 1.45; max-width: 85%; white-space: pre-wrap; }
.aig-msg--user { align-self: flex-end; background: var(--aig-primary, #8b5cf6); color: var(--aig-on-primary, #fff); }
.aig-msg--agent { align-self: flex-start; background: #f1f5f9; }
/* A note from the panel itself - dictation failing, say - not a line the assistant spoke.
   Centred, small and unboxed, so it never reads as one side of the conversation. */
.aig-notice { align-self: center; max-width: 90%; padding: 2px 0; font-size: 11px; line-height: 1.4; color: #94a3b8; text-align: center; }
/* The wait is the honest part of this: the answer is not being written yet, it is being
   thought about, so the placeholder is three resting dots rather than a fake cursor. */
.aig-typing { align-self: flex-start; background: #f1f5f9; border-radius: 10px; padding: 10px 12px; display: flex; gap: 4px; }
.aig-typing span { width: 6px; height: 6px; border-radius: 50%; background: #94a3b8; animation: aig-blink 1.4s infinite ease-in-out both; }
.aig-typing span:nth-child(2) { animation-delay: .18s; }
.aig-typing span:nth-child(3) { animation-delay: .36s; }
@keyframes aig-blink { 0%, 80%, 100% { opacity: .28; } 40% { opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .aig-typing span { animation: none; opacity: .6; } }
/* The composer itself is shared with the configurator panel (composer.ts → COMPOSER_CSS),
   so only what is specific to this panel lives here: the accent it borrows from the
   customer's colour, and the inset that keeps it clear of the panel's edges. */
:host { --aigc-accent: var(--aig-primary-ink, #7a51d8); }
.aigc-form { margin: 0 10px 6px; }
/* Buttons and links do only match :focus-visible on keyboard, so a real ring is right
   there. Drawn inset (negative offset) so it never escapes the panel's rounded corners;
   white on the header and launcher because those sit on the customer's own colour, which
   an indigo ring could match exactly and vanish into. */
.aig-close:focus-visible,
.aig-toggle:focus-visible { outline: 2px solid var(--aig-on-primary, #fff); outline-offset: -3px; }
.aig-footer a:focus-visible { outline: 2px solid var(--aig-primary-ink, #7a51d8); outline-offset: 2px; border-radius: 4px; }
/* The hairline above the attribution rides on the footer element itself, which only
   exists when poweredBy is on - so a white-label panel loses the line together with
   the line of text, and no separate rule has to remember the two go together.
   Lighter than the composer's own border: it separates, it does not divide. */
/* One flat line, deliberately: no brand lockup, no weight, no accent colour. This sits on
   someone else's site under their conversation, and it is the least important thing in the
   panel - it should be findable, not noticeable. Anything that made it a logo (a heavier
   name, an indigo [AI]) pulled the eye away from the answer above it. */
.aig-footer { flex-shrink: 0; padding: 4px 0; text-align: center; font-size: 9.5px; border-top: 1px solid #f1f5f9; }
.aig-footer a { color: #94a3b8; font-weight: 400; text-decoration: none; }
.aig-footer a:hover { text-decoration: underline; }
`;

// Marca de brand (cristalul-cometă) desenată dintr-o singură culoare la opacități
// diferite pe fețe: fundalul butonului e `--aig-primary`, adică orice culoare aleasă
// de client, iar un gradient indigo ar bate cu ea. Culoarea e `currentColor`, nu alb
// fix - pe o bulină deschisă cerneala devine închisă (vezi `inkOnPrimary`) și o cometă
// albă ar dispărea. Fallback pt. toggle: cât timp clientul nu are logo propriu - sau
// logo-ul lui nu se încarcă - vezi mount().
const TOGGLE_MARK_SVG =
  '<svg width="26" height="26" viewBox="0 0 64 64" aria-hidden="true">' +
  '<defs>' +
  '<linearGradient id="aig-tr" x1="0" y1="1" x2="0" y2="0">' +
  '<stop offset="0" stop-color="currentColor" stop-opacity=".5"/>' +
  '<stop offset=".55" stop-color="currentColor" stop-opacity=".16"/>' +
  '<stop offset="1" stop-color="currentColor" stop-opacity="0"/></linearGradient>' +
  '<radialGradient id="aig-gl" cx=".5" cy=".5" r=".5">' +
  '<stop offset="0" stop-color="currentColor" stop-opacity=".26"/>' +
  '<stop offset="1" stop-color="currentColor" stop-opacity="0"/></radialGradient>' +
  '</defs>' +
  '<circle cx="32" cy="32" r="29" fill="url(#aig-gl)"/>' +
  '<g transform="rotate(38 32 32)"><g transform="translate(32 32) scale(.95) translate(-32 -32)">' +
  '<g transform="translate(0 4)">' +
  '<path fill="url(#aig-tr)" d="M28.4 14 Q30.4 2 32 -12 Q33.6 2 35.6 14 Z"/>' +
  '<path fill="currentColor" fill-opacity=".68" d="M27.9 12.8 L32 8 L32 56 L26.4 51.2 Z"/>' +
  '<path fill="currentColor" fill-opacity=".97" d="M32 8 L36.1 12.8 L37.6 51.2 L32 56 Z"/>' +
  '</g></g></g></svg>';

// Logo-ul de brand implicit din buton: badge-ul raster (același ca landing/favicon/
// social), ca imagine data-URI inline - se afișează cât timp clientul n-a pus logo
// propriu ȘI n-a ales o culoare custom (stare 100% default). Inline, nu URL: nicio
// cerere externă în plus pe situl clientului. Când clientul ARE o culoare custom dar
// fără logo, rămâne `TOGGLE_MARK_SVG` (cristalul care se colorează după culoarea lui).
const DEFAULT_TOGGLE_LOGO =
  "data:image/png;base64,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";

/** Cât de sus poate crește compozitorul înainte să înceapă să deruleze (≈5 rânduri). */
const MAX_COMPOSER_PX = 108;

/**
 * Ce se întâmplă când dictarea nu merge. Ajung ca notă discretă, NU ca replică a
 * asistentului: n-a spus el asta, iar o bulă de agent ar minți despre cine vorbește.
 * Din același motiv nu intră nici în istoricul conversației.
 */
const MIC_ERROR_KEYS: Record<MicErrorKind, keyof ChromeCopy> = {
  denied: "micDenied",
  "no-speech": "micNoSpeech",
  network: "micNetwork",
  failed: "micFailed",
};

export type MessageRole = "user" | "agent";

export class Widget {
  private root!: HTMLDivElement;
  /** Rădăcina shadow: tot ce desenăm trăiește aici, nu în DOM-ul sitului. */
  private shadow!: ShadowRoot;
  private panel!: HTMLDivElement;
  private toggle!: HTMLButtonElement;
  private messages!: HTMLDivElement;
  /** Compozitorul comun cu panoul de configurare - vezi composer.ts. */
  private composer!: Composer;
  /** Bula de „gândește", cât timp răspunsul e pe drum. */
  private typing?: HTMLDivElement;
  /** Oprește derularea progresivă a ultimului răspuns, dacă e în curs. */
  private cancelReveal?: () => void;
  private askHandler?: (question: string) => void;
  private stopHandler?: () => void;
  private closeHandler?: () => void;
  private openHandler?: () => void;
  private resizeObserver?: ResizeObserver;
  private saveGeometryTimer?: ReturnType<typeof setTimeout>;
  /** Deplasarea curentă cerută de spotlight (vezi `shiftPanel`). */
  private shiftX = 0;
  private shiftY = 0;
  /** Panoul e coborât până rămâne doar headerul (telefon) - vezi `setDocked`. */
  private docked = false;
  private dockReleaseListener?: () => void;
  /**
   * Stratul de prezență, dacă e aprins. Public pentru modul de voce, care are
   * nevoie să-i împingă nivelul audio direct (`feedAudio`) - bucla de desen nu
   * trebuie să treacă prin panoul de chat.
   */
  presence?: Presence;
  /**
   * Gazda proprie a stratului de prezență, FRATE cu `.aig-root`, nu descendent -
   * vezi motivul la crearea ei în `mount()`.
   */
  private presenceHost?: HTMLDivElement;
  private voiceBtn?: HTMLButtonElement;
  private voiceHandler?: () => void;

  constructor(
    private readonly appId: string,
    /** Growth: linia „Powered by" rămâne (achiziție organică); Agency/Enterprise (white-label): dispare. */
    private readonly poweredBy: boolean = true,
    /** Personalizare widget (Growth+) - gol pentru Starter/site-uri fără cont, vezi runtime.ts resolveBranding. */
    private readonly branding: WidgetBranding = {},
    /**
     * Stratul de prezență (orb + waveform). Implicit STINS: e pregătirea modului
     * de voce și se aprinde deocamdată doar pe staging, prin `?aig_presence=1`
     * (vezi `sdk.ts`). Cât timp e stins, nimic din el nu ajunge în pagină.
     */
    private readonly presenceEnabled: boolean = false,
    /**
     * `business.language` din configul appului: limba în care panoul îi
     * vorbește vizitatorului, de la placeholder până la `aria-label`-uri.
     * Vezi `uiText.ts`.
     */
    private readonly language: string | undefined = undefined,
  ) {}

  /** Textele scrise de noi, în limba appului. Un singur tabel, vezi `uiText.ts`. */
  private get copy(): ChromeCopy {
    return chromeCopy(this.language);
  }

  mount(): void {
    this.root = document.createElement("div");
    this.root.className = "aig-root";
    // O regulă globală a paginii gazdă (`* { pointer-events: none }`, sau orice
    // altă cauză) ar putea întinde efectul și peste containerul nostru - stă
    // direct sub <body>. Suprascriem aici, inline, ca să bată orice regulă a
    // paginii; conținutul din shadow root oricum nu poate fi atins din light DOM.
    this.root.style.setProperty("pointer-events", "auto", "important");
    const shadow = this.root.attachShadow({ mode: "open" });
    this.shadow = shadow;

    const style = document.createElement("style");
    style.setAttribute("data-ai-guidance", "widget-css");
    // Compozitorul întâi: regulile panoului vin după, ca să poată suprascrie la
    // specificitate egală (marginea din `.aigc-form`).
    style.textContent = COMPOSER_CSS + WIDGET_CSS;
    shadow.appendChild(style);
    if (this.branding.primaryColor) {
      this.root.style.setProperty("--aig-primary", this.branding.primaryColor);
      this.root.style.setProperty("--aig-primary-hover", darkenHex(this.branding.primaryColor, 0.12));
      this.root.style.setProperty("--aig-primary-ink", inkOnSurface(this.branding.primaryColor));
    }
    // Cerneala așezată PESTE culoarea primară (text de header, bula vizitatorului,
    // butonul ✕): `textColor` explicit al proprietarului dacă există, altfel calculată
    // din primaryColor pentru contrast AA (un pastel pe care albul devine ilizibil).
    // Se setează chiar și fără primaryColor - indigo-ul implicit rămâne fundal, dar
    // textul tot pe el stă. `--aig-on-primary-soft` (fundalul lui ✕) urmează aceeași
    // cerneală la alfa mic, derivat din RGB (nu binar alb/negru) ca o textColor de
    // orice culoare să dea o nuanță coerentă.
    const ink =
      this.branding.textColor ??
      (this.branding.primaryColor ? inkOnPrimary(this.branding.primaryColor) : undefined);
    if (ink) {
      this.root.style.setProperty("--aig-on-primary", ink);
      const [r, g, b] = hexToRgb(ink);
      this.root.style.setProperty("--aig-on-primary-soft", `rgba(${r},${g},${b},.16)`);
      this.root.style.setProperty("--aig-on-primary-soft-hover", `rgba(${r},${g},${b},.28)`);
    }
    // CSS custom al proprietarului (doar white-label; backendul l-a filtrat deja din
    // branding dacă planul nu permite - vezi widgetBrandingFor). Injectat prin
    // `textContent`: nu e parsat ca HTML, deci un `</style>` nu închide nimic și CSS-ul
    // nu execută JS; blast-radius = shadow root-ul widgetului. Appended DUPĂ stilul
    // nostru ca să câștige la specificitate egală (`.aig-toggle` etc.).
    const customCss = [this.branding.launcherCss, this.branding.chatCss].filter(Boolean).join("\n");
    if (customCss) {
      const customStyle = document.createElement("style");
      customStyle.setAttribute("data-ai-guidance", "custom-css");
      customStyle.textContent = customCss;
      shadow.appendChild(customStyle);
    }

    // Stratul de prezență NU intră în shadow root-ul de mai sus, deși ar fi mai
    // simplu: `.aig-root` (`this.root`) e el însuși `position: fixed`, iar unele
    // situri de test (demo3, Shopify) îi pun un `transform` din CSS-ul paginii ca
    // să sară peste bara de preview. Un `transform` pe orice strămoș devine
    // containing block pentru descendenții `position: fixed` - regulă CSS care nu
    // se oprește la granița shadow DOM, fiindcă se calculează pe arborele
    // aplatizat. Dacă orbul ar fi copil (chiar și prin shadow root) al
    // `.aig-root`, „fixed jos-centru" s-ar calcula față de `.aig-root`
    // transformat, nu față de fereastră - exact stricăciunea raportată pe demo3.
    // Soluție: gazdă proprie, FRATE cu `.aig-root`, atașată direct la <body>, cu
    // shadow root propriu (izolarea de CSS-ul gazdei rămâne neschimbată).
    if (this.presenceEnabled) {
      this.presence = new Presence({ primaryColor: this.branding.primaryColor });
      this.presenceHost = document.createElement("div");
      this.presenceHost.className = "aig-presence-host";
      const presenceShadow = this.presenceHost.attachShadow({ mode: "open" });
      this.presence.mount(presenceShadow);
    }

    this.panel = document.createElement("div");
    this.panel.className = "aig-panel";
    this.panel.hidden = true;

    const header = document.createElement("div");
    header.className = "aig-header";
    const headerLeft = document.createElement("span");
    headerLeft.className = "aig-header-left";
    // Decizie de produs: doar numele în header, niciodată un logo - vezi
    // dashboard-ul (WidgetPreviewMock), unde previzualizarea reflectă asta.
    // `branding.logoUrl` rămâne stocat/încărcabil pentru o folosire viitoare.
    const title = document.createElement("span");
    title.className = "aig-header-title";
    title.textContent = this.branding.assistantName || this.copy.assistantName;
    headerLeft.appendChild(title);
    const closeBtn = document.createElement("button");
    closeBtn.className = "aig-close";
    closeBtn.type = "button";
    closeBtn.setAttribute("aria-label", this.copy.close);
    closeBtn.textContent = "✕";
    closeBtn.addEventListener("click", () => this.close());

    // Butonul de voce apare doar unde stratul de prezență chiar există: fără
    // orb n-ar avea ce conduce, iar o conversație vocală fără niciun indiciu
    // vizual că e ascultată e mai rea decât lipsa ei.
    if (this.presence) {
      this.voiceBtn = document.createElement("button");
      this.voiceBtn.className = "aig-close aig-voice";
      this.voiceBtn.type = "button";
      this.voiceBtn.setAttribute("aria-label", this.copy.voiceStart);
      this.voiceBtn.textContent = "🎙";
      this.voiceBtn.addEventListener("click", () => this.voiceHandler?.());
      header.append(headerLeft, this.voiceBtn, closeBtn);
    } else {
      header.append(headerLeft, closeBtn);
    }
    this.attachDrag(header);

    this.messages = document.createElement("div");
    this.messages.className = "aig-messages";

    // Enter trimite, Shift+Enter trece pe rând nou - convenția oricărui chat pe care
    // vizitatorul a folosit-o deja; pe touch nu, vezi composer.ts. Microfonul apare
    // doar unde browserul chiar are dictare ȘI contextul e securizat - pe un sit al
    // clientului rămas pe http nu se desenează deloc, vezi dictationSupported().
    this.composer = new Composer({
      labels: {
        placeholder: this.copy.placeholder,
        send: this.copy.send,
        stop: this.copy.stop,
        mic: this.copy.mic,
        micListening: this.copy.micListening,
      },
      maxLength: 1000,
      maxHeightPx: MAX_COMPOSER_PX,
      stoppable: true,
      mic: true,
      onSubmit: (question) => this.askHandler?.(question),
      onStop: () => this.stopHandler?.(),
      onMicError: (kind) => this.addNotice(this.copy[MIC_ERROR_KEYS[kind]]),
    });

    this.panel.append(header, this.messages, this.composer.form);

    if (this.poweredBy) {
      const footer = document.createElement("div");
      footer.className = "aig-footer";
      const link = document.createElement("a");
      link.href = "https://askmywebsiteai.com";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      // Un singur text, nu lockup-ul de pe landing: aici atribuirea stă sub conversația
      // altcuiva și trebuie să fie găsibilă, nu vizibilă. Prin `textContent`, ca
      // invariantul „niciun HTML brut" să țină.
      link.textContent = "Powered by AskMyWebsite[AI]";
      footer.appendChild(link);
      this.panel.appendChild(footer);
    }

    this.toggle = document.createElement("button");
    this.toggle.className = "aig-toggle";
    this.toggle.type = "button";
    this.toggle.setAttribute("aria-label", this.copy.open);
    if (this.branding.logoUrl) {
      // „Just logo": scoatem cercul/fundalul/umbra și lăsăm doar imaginea. Doar cu logo
      // prezent - marca de fallback are nevoie de bulina colorată ca să se vadă.
      if (this.branding.logoOnly) this.toggle.classList.add("aig-toggle--logo-only");
      const logo = document.createElement("img");
      logo.className = "aig-toggle-logo";
      logo.src = this.branding.logoUrl;
      logo.alt = "";
      // Logo mort (șters, domeniu picat, mixed-content) → nu lăsăm iconița spartă a
      // browserului pe situl clientului, revenim la marca noastră ȘI la bulina colorată:
      // fără fundal, marca de fallback ar pluti invizibilă pe multe pagini.
      logo.onerror = () => {
        logo.remove();
        this.toggle.classList.remove("aig-toggle--logo-only");
        // dacă în DB a rămas și o iconiță predefinită, e o alegere a clientului -
        // cădem pe ea, nu pe marca noastră
        this.toggle.innerHTML =
          (this.branding.presetIcon && PRESET_TOGGLE_ICONS[this.branding.presetIcon]) ||
          TOGGLE_MARK_SVG;
      };
      this.toggle.appendChild(logo);
    } else if (this.branding.presetIcon && PRESET_TOGGLE_ICONS[this.branding.presetIcon]) {
      // Iconiță predefinită aleasă în dashboard (presetIcons.ts): desenată cu
      // cerneala butonului (currentColor), deci lizibilă pe orice culoare primară.
      // Doar fără logo propriu - logo-ul are mereu prioritate.
      this.toggle.innerHTML = PRESET_TOGGLE_ICONS[this.branding.presetIcon];
    } else if (!this.branding.primaryColor) {
      // Stare 100% default (fără logo propriu ȘI fără culoare custom): badge-ul nostru
      // de brand ca imagine, identic cu landing/favicon/social.
      const logo = document.createElement("img");
      logo.className = "aig-toggle-logo";
      logo.src = DEFAULT_TOGGLE_LOGO;
      logo.alt = "";
      this.toggle.appendChild(logo);
    } else {
      // Culoare custom fără logo propriu → marca adaptivă, care se colorează după
      // culoarea aleasă de client (un badge violet fix ar bate cu ea).
      this.toggle.innerHTML = TOGGLE_MARK_SVG;
    }
    this.toggle.addEventListener("click", () => this.open());

    shadow.append(this.panel, this.toggle);
    document.body.appendChild(this.root);
    // FRATE cu `.aig-root`, nu copil - vezi comentariul de la crearea ei mai sus.
    if (this.presenceHost) document.body.appendChild(this.presenceHost);

    this.applyStoredGeometry();
    this.observeResize();
    // Panoul rămas deschis peste o navigare se redeschide CU focus pe input:
    // vizitatorul tocmai conversa (de regulă chiar navigarea a venit din chat)
    // și se așteaptă să scrie direct, nu să dea click întâi (feedback Radu,
    // 27 aug 2026). Doar pe pointer fin - pe touch, focusul ar ridica
    // tastatura peste pagina abia deschisă, la fiecare navigare.
    if (loadWidgetState(this.appId).open) this.setOpen(true, !this.coarsePointer());
  }

  /** Ecran touch (telefon/tabletă)? `matchMedia` lipsă = desktop (jsdom, browsere vechi). */
  private coarsePointer(): boolean {
    return typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
  }

  /**
   * Ascunde/arată întregul widget (launcher + panou) fără să-l demonteze -
   * folosit de excluderea pe URL (sdk.ts, exclusion.ts): pe un SPA pagina
   * exclusă e o stare tranzitorie, iar demontarea ar pierde starea panoului
   * și firul conversației la întoarcerea pe o pagină permisă.
   */
  setHidden(hidden: boolean): void {
    if (!this.root) return;
    // Clasă, NU stil inline: la !important, cascada de shadow scoping dă câștig
    // arborelui INTERIOR peste inline-ul de pe host, deci un display:none inline
    // pierdea în fața lui `:host { display:block !important }` din WIDGET_CSS și
    // widgetul rămânea vizibil pe paginile excluse (bug prins pe prod, 26 iul
    // 2026 - jsdom nu implementează cascada asta, testele nu-l puteau vedea).
    this.root.classList.toggle("aig-hidden", hidden);
  }

  /** Aplică lățimea/înălțimea și poziția salvate dintr-un drag/resize anterior (dacă există). */
  private applyStoredGeometry(): void {
    const state = loadWidgetState(this.appId);
    if (state.width) this.panel.style.width = `${state.width}px`;
    if (state.height) this.panel.style.height = `${state.height}px`;
    if (state.left != null && state.top != null) {
      this.panel.style.left = `${state.left}px`;
      this.panel.style.top = `${state.top}px`;
      this.panel.style.right = "auto";
      this.panel.style.bottom = "auto";
    }
  }

  /**
   * Deplasarea vizibilă ACUM, nu cea cerută: cât timp alunecarea e în curs,
   * `transform`-ul calculat e la jumătatea drumului, iar `shiftX/shiftY` arată
   * deja destinația. Diferența nu e teoretică - vezi `panelRect`.
   *
   * Browserele rezolvă `transform` la o matrice; jsdom întoarce textul inline
   * nemodificat, deci acolo cădem pe valoarea cerută (fără tranziții, e aceeași).
   */
  private visibleShift(): { x: number; y: number } {
    const value = getComputedStyle(this.panel).transform;
    const matrix = /^matrix(3d)?\(([^)]+)\)$/.exec(value);
    if (!matrix) return { x: this.shiftX, y: this.shiftY };
    const parts = matrix[2].split(",").map((n) => parseFloat(n));
    const [x, y] = matrix[1] ? [parts[12], parts[13]] : [parts[4], parts[5]];
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : { x: this.shiftX, y: this.shiftY };
  }

  /**
   * Unde stă panoul CÂND E ACASĂ, oricât de deplasat ar fi în clipa asta -
   * contractul din `guide.ts` (`ChatObstacle.rect`). `null` cu panoul închis:
   * atunci nu e nimic în calea inelului și n-ar avea cine citi replica.
   *
   * Scăderea se face din deplasarea VIZIBILĂ, nu din cea cerută, și asta e tot
   * miezul: fiecare inel se stinge cerând revenirea acasă, iar revenirea e o
   * tranziție de 420ms. Următorul highlight cade adesea în mijlocul ei - cu
   * `shiftX` deja pe zero și panoul încă vizibil la jumătatea drumului, am fi
   * raportat drept „acasă" o cutie în care panoul nu stă, am fi conchis „nu e
   * nimic în cale" și nu l-am fi mutat deloc. Al doilea pas ghidat rămânea sub
   * chat, tăcut. (Găsit în browser real: jsdom nu animă, deci nu poate arăta asta.)
   */
  panelRect(): { left: number; top: number; right: number; bottom: number } | null {
    if (this.panel.hidden) return null;
    const rect = this.panel.getBoundingClientRect();
    const shift = this.visibleShift();
    return {
      left: rect.left - shift.x,
      top: rect.top - shift.y,
      right: rect.right - shift.x,
      bottom: rect.bottom - shift.y,
    };
  }

  /**
   * Se dă la o parte: elementul evidențiat era în spatele panoului. Direcția vine
   * din `guide.ts`, care are geometria; aici rămâne doar aplicarea ei - fără nicio
   * bulă de chat (eliminată la cererea lui Radu, 1 aug 2026).
   */
  shiftPanel(dx: number, dy: number): void {
    if (dx === this.shiftX && dy === this.shiftY) return;
    this.shiftX = dx;
    this.shiftY = dy;
    this.panel.style.transform = dx === 0 && dy === 0 ? "" : `translate(${dx}px, ${dy}px)`;
  }

  /**
   * Starea „dochat": panoul a fost coborât de ghidare (prin `shiftPanel`) și
   * doar headerul mai e pe ecran. Restul conținutului devine `inert`, ca tastatura
   * și cititorul de ecran să nu ajungă în ceva ce nu se vede. Atingerea
   * headerului îl redeschide (`attachDrag` → `releaseDock`).
   */
  setDocked(docked: boolean): void {
    if (docked === this.docked) return;
    this.docked = docked;
    this.panel.classList.toggle("aig-panel--docked", docked);
    for (const child of Array.from(this.panel.children)) {
      if (child.classList.contains("aig-header")) continue;
      if (docked) child.setAttribute("inert", "");
      else child.removeAttribute("inert");
    }
  }

  /** Ghidarea își notează că vizitatorul a redeschis panoul dochat. */
  onDockRelease(listener: () => void): void {
    this.dockReleaseListener = listener;
  }

  /** Atingerea headerului dochat: panoul revine acasă și ghidarea nu-l mai coboară pentru inelul curent. */
  private releaseDock(): void {
    this.setDocked(false);
    this.shiftPanel(0, 0);
    this.dockReleaseListener?.();
  }

  /**
   * Coboară deplasarea curentă în `left/top` și șterge transformul, fără salt
   * vizual și fără să anime revenirea la zero. Chemată când vizitatorul apucă
   * panoul de header: din clipa aia poziția e a lui, iar `attachDrag` citește
   * `getBoundingClientRect` ca punct de plecare - cu un transform activ ar fi
   * pornit dintr-un loc și ar fi sărit în altul.
   */
  private bakeShift(): void {
    if (this.shiftX === 0 && this.shiftY === 0) return;
    const rect = this.panel.getBoundingClientRect();
    this.panel.style.transition = "none";
    this.panel.style.left = `${rect.left}px`;
    this.panel.style.top = `${rect.top}px`;
    this.panel.style.right = "auto";
    this.panel.style.bottom = "auto";
    this.panel.style.transform = "";
    this.shiftX = 0;
    this.shiftY = 0;
    // Reflow forțat înainte de a reda tranziția: altfel browserul coalesează
    // cele două scrieri și animă totuși revenirea transformului la zero.
    void this.panel.offsetWidth;
    this.panel.style.transition = "";
  }

  /** Rescroll-ul din colțul din dreapta jos (CSS `resize`) - persistăm dimensiunea rezultată, cu debounce. */
  private observeResize(): void {
    this.resizeObserver = new ResizeObserver(() => {
      if (this.panel.hidden) return;
      clearTimeout(this.saveGeometryTimer);
      this.saveGeometryTimer = setTimeout(() => {
        saveWidgetState(this.appId, {
          width: Math.round(this.panel.offsetWidth),
          height: Math.round(this.panel.offsetHeight),
        });
      }, 200);
    });
    this.resizeObserver.observe(this.panel);
  }

  /** Drag pe header: trece panoul pe poziționare explicită left/top și persistă poziția finală. */
  private attachDrag(handle: HTMLElement): void {
    handle.addEventListener("pointerdown", (e) => {
      if ((e.target as HTMLElement).closest(".aig-close")) return;
      e.preventDefault();
      // Dochat, headerul e un mâner de redeschis, nu de tras: panoul stă în mare
      // parte sub marginea ecranului, iar un drag ar coborî în left/top (și în
      // storage) o poziție pe care nimeni n-a ales-o.
      if (this.docked) {
        this.releaseDock();
        return;
      }
      // Înainte de orice măsurătoare: din clipa asta poziția e a vizitatorului.
      this.bakeShift();
      const rect = this.panel.getBoundingClientRect();
      const startX = e.clientX;
      const startY = e.clientY;
      const startLeft = rect.left;
      const startTop = rect.top;
      this.panel.style.right = "auto";
      this.panel.style.bottom = "auto";
      handle.setPointerCapture(e.pointerId);

      const onMove = (ev: PointerEvent): void => {
        const maxLeft = Math.max(0, window.innerWidth - this.panel.offsetWidth);
        const maxTop = Math.max(0, window.innerHeight - this.panel.offsetHeight);
        const left = Math.min(Math.max(0, startLeft + (ev.clientX - startX)), maxLeft);
        const top = Math.min(Math.max(0, startTop + (ev.clientY - startY)), maxTop);
        this.panel.style.left = `${left}px`;
        this.panel.style.top = `${top}px`;
      };
      const onUp = (): void => {
        handle.removeEventListener("pointermove", onMove);
        const finalRect = this.panel.getBoundingClientRect();
        saveWidgetState(this.appId, { left: Math.round(finalRect.left), top: Math.round(finalRect.top) });
      };
      handle.addEventListener("pointermove", onMove);
      handle.addEventListener("pointerup", onUp, { once: true });
    });
  }

  onAsk(handler: (question: string) => void): void {
    this.askHandler = handler;
  }

  /** Apăsarea butonului cât timp răspunsul e pe drum. Vezi `runAsk` din sdk.ts. */
  onStop(handler: () => void): void {
    this.stopHandler = handler;
  }

  /**
   * Închiderea EXPLICITĂ a panoului (butonul ✕). Deliberat pe `close()`, nu pe
   * `setOpen(false)`: acela rulează și la montare, când starea salvată zice
   * „închis", iar atunci n-a închis nimeni nimic.
   *
   * Vizitatorul care închide asistentul a terminat cu el. Vezi sdk.ts: stingem
   * inelul rămas pe pagină și dezarmăm continuarea, ca un click ulterior să nu
   * redeschidă panoul cu un pas dintr-o conversație pe care omul a încheiat-o.
   */
  onClose(handler: () => void): void {
    this.closeHandler = handler;
  }

  /**
   * Panoul s-a deschis. Spre deosebire de `onClose`, e legat de `setOpen(true)`,
   * NU doar de click-ul pe bulă: redeschiderea automată din starea salvată la
   * montare (`loadWidgetState().open`, după o navigare în același tab) e tot o
   * „deschidere" din perspectiva vizitatorului, iar pâlnia de sesiune numără
   * exact asta. Serverul deduplică oricum pe (app, sesiune, zi), deci un semnal
   * în plus nu poate umfla cifra.
   */
  onOpen(handler: () => void): void {
    this.openHandler = handler;
  }

  /**
   * Notă a panoului, nu replică a asistentului - și de-aia NU trece prin `addMessage`:
   * aceea desenează o bulă de conversație, iar apelanții ei o și salvează în istoric
   * (vezi sdk.ts). O eroare de microfon n-are ce căuta în conversația reluată la reload.
   * Înlocuiește nota anterioară, ca apăsările repetate pe microfon să nu stivuiască.
   */
  /** Public și pentru modul de voce, care raportează prin același canal. */
  /**
   * Notița deterministă a mascării (31 aug 2026, „garantat 100%", cerere Radu):
   * nota din prompt îi cere modelului să descurajeze repetarea datelor
   * sensibile, dar formularea aia e a modelului. ASTA nu e: apare mereu,
   * mecanic, la orice mesaj în care redactSensitive a mascat ceva - aceeași
   * bandă de notiță ca erorile de dictare, nu o replică a asistentului.
   */
  noticeSensitiveRedacted(): void {
    this.addNotice(this.copy.sensitiveRedacted);
  }

  addNotice(text: string): void {
    this.messages.querySelector(".aig-notice")?.remove();
    const note = document.createElement("div");
    note.className = "aig-notice";
    note.setAttribute("role", "status");
    note.textContent = text;
    this.messages.appendChild(note);
    this.messages.scrollTop = this.messages.scrollHeight;
  }

  addMessage(role: MessageRole, text: string, opts: { reveal?: boolean } = {}): void {
    const msg = document.createElement("div");
    msg.className = `aig-msg aig-msg--${role}`;
    this.messages.appendChild(msg);
    // Curățarea tagului expresiv se face AICI, la randare, ca să prindă și
    // replicile vocale live, și pe cele restaurate din istoricul local (care
    // rămâne brut, exact ca textul trimis spre sinteză). Vezi `voice/tags.ts`.
    const shown = stripExpressiveTag(text);
    if (opts.reveal) this.revealInto(msg, shown);
    else msg.textContent = shown;
    this.messages.scrollTop = this.messages.scrollHeight;
  }

  /**
   * Un stil suplimentar în shadow root, după al nostru (deci poate suprascrie la
   * specificitate egală, ca `chatCss`). Folosit de partea de commerce, care e
   * încărcată doar pe appuri de magazin - pe restul siturilor CSS-ul ei nu ajunge
   * niciodată în pagină.
   */
  addStyle(css: string): void {
    const style = document.createElement("style");
    style.setAttribute("data-ai-guidance", "commerce-css");
    style.textContent = css;
    this.shadow.appendChild(style);
  }

  /**
   * Un nod care plutește peste situl clientului (panoul de carduri al modului de
   * voce, toast-urile de coș). În shadow root, nu în light DOM: aceeași izolare
   * de CSS-ul gazdei ca panoul, și - la fel de important - conținutul lui nu
   * intră în snapshotul trimis modelului.
   */
  mountOverlay(node: HTMLElement): void {
    this.shadow.appendChild(node);
  }

  /**
   * Conținut bogat în firul conversației (carduri de produs). Separat de
   * `addMessage`: nu e o replică, deci nu are bulă și nu se salvează în istoric -
   * la reload cardurile ar arăta prețuri și stocuri vechi.
   */
  addCardsToThread(node: HTMLElement): void {
    this.messages.appendChild(node);
    this.messages.scrollTop = this.messages.scrollHeight;
  }

  /**
   * Răspunsul apare pe bucăți, nu dintr-o dată. Nu e streaming - textul e deja
   * întreg aici - ci ritmul de citire: un perete de text care apare instantaneu
   * se citește mai greu decât unul care se așază. De-aia derularea are un plafon
   * de durată: un răspuns lung nu are voie să întârzie cititul, doar să-l ghideze.
   */
  private revealInto(el: HTMLElement, text: string): void {
    this.cancelReveal?.();
    const reduced = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || text.length < 40) {
      el.textContent = text;
      return;
    }
    this.presence?.setState("speaking");
    const startedAt = performance.now();
    const duration = Math.min(900, 220 + text.length * 4);
    let frame = 0;
    const step = (now: number): void => {
      const progress = Math.min(1, (now - startedAt) / duration);
      // câte caractere sunt vizibile acum; ne oprim la limita de cuvânt ca textul
      // să nu pară că se taie în mijlocul unui cuvânt
      const upto = Math.floor(text.length * progress);
      const boundary = progress === 1 ? text.length : text.lastIndexOf(" ", upto);
      el.textContent = text.slice(0, boundary > 0 ? boundary : upto);
      this.messages.scrollTop = this.messages.scrollHeight;
      if (progress < 1) frame = requestAnimationFrame(step);
      else {
        this.cancelReveal = undefined;
        this.presence?.setState("idle");
      }
    };
    frame = requestAnimationFrame(step);
    // Oprirea NU trunchiază: textul e deja primit și contorizat, deci ascunderea
    // restului ar fi o pierdere pentru vizitator. Stop încheie animația, atât.
    this.cancelReveal = () => {
      cancelAnimationFrame(frame);
      el.textContent = text;
      this.messages.scrollTop = this.messages.scrollHeight;
      this.cancelReveal = undefined;
      this.presence?.setState("idle");
    };
  }

  /** Sare peste derularea în curs, dacă există. Apelat de Stop. */
  finishReveal(): void {
    this.cancelReveal?.();
  }

  setBusy(busy: boolean): void {
    // Câmpul dezactivat își pierde focusul; compozitorul îl reține și îl dă înapoi
    // când răspunsul a sosit - altfel ar trebui un click în plus la fiecare tură.
    this.composer.setBusy(busy);
    // În chatul text nu există `listening`; `speaking` îl pune `revealInto`, cât
    // timp răspunsul se așază. Legătura asta face stratul verificabil ACUM, fără
    // niciun provider de voce conectat.
    if (busy) this.presence?.setState("processing");
    else this.presence?.setState("idle");
    if (busy) {
      this.typing = document.createElement("div");
      this.typing.className = "aig-typing";
      this.typing.setAttribute("aria-label", this.copy.thinking);
      this.typing.append(...[0, 1, 2].map(() => document.createElement("span")));
      this.messages.appendChild(this.typing);
      this.messages.scrollTop = this.messages.scrollHeight;
    } else {
      this.typing?.remove();
      this.typing = undefined;
    }
  }

  private setOpen(open: boolean, focus = false): void {
    this.panel.hidden = !open;
    this.toggle.hidden = open;
    // Prezența NU urmează panoul: ea aparține modului de voce, care o aprinde și
    // o stinge singur (vezi voice/controller.ts). Închiderea panoului o stinge
    // totuși, ca vizitatorul să nu rămână cu un orb orfan pe pagină după ce a
    // închis asistentul.
    if (!open) this.presence?.hide();
    saveWidgetState(this.appId, { open });
    if (open) this.openHandler?.();
    if (open && focus) this.composer.focus();
  }

  /** Click pe microfonul din header - pornește sau oprește modul de voce. */
  onVoiceToggle(handler: () => void): void {
    this.voiceHandler = handler;
  }

  /** Marchează butonul cât timp conversația vocală e deschisă. */
  setVoiceActive(active: boolean): void {
    if (!this.voiceBtn) return;
    this.voiceBtn.classList.toggle("aig-voice--on", active);
    this.voiceBtn.setAttribute("aria-label", active ? this.copy.voiceEnd : this.copy.voiceStart);
  }

  /**
   * Panoul e deschis ACUM. Citit de motorul de proactivitate: cât timp
   * conversația e pe ecran, niciun nudge nu se mai afișează (ar fi asistentul
   * bâzâindu-și propriul interlocutor).
   */
  isOpen(): boolean {
    return !this.panel.hidden;
  }

  open(): void {
    this.setOpen(true, true);
  }

  close(): void {
    this.setOpen(false);
    this.closeHandler?.();
  }
}
